import { z } from 'zod';
import { INSTALLMENT_STATUSES } from '../finance';
import { PageQuerySchema, pageOf } from './common';

export const PAYMENT_METHODS = ['cash', 'cheque', 'transfer', 'online'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_STATUSES = ['pending', 'cleared', 'rejected', 'cancelled'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];
export const REMINDER_CHANNELS = ['email', 'sms', 'whatsapp'] as const;
export type ReminderChannel = (typeof REMINDER_CHANNELS)[number];

const Millimes = z.number().int();
const PositiveMillimes = z.number().int().positive().max(100_000_000);
const IsoDate = z.iso.date();

// ── Fee schedules ───────────────────────────────────────────────────────────
export const FeeTrancheSchema = z.object({
  label: z.string().trim().min(1).max(60),
  /** Sub-label, e.g. « Inscription ». */
  note: z.string().trim().max(60).nullable().default(null),
  dueOn: IsoDate,
  amountMillimes: PositiveMillimes,
});
export type FeeTranche = z.infer<typeof FeeTrancheSchema>;

export const FeeScheduleSchema = z.object({
  id: z.uuid(),
  academicYearId: z.uuid(),
  yearLabel: z.string(),
  /** null = default schedule for every programme without its own. */
  programId: z.uuid().nullable(),
  programCode: z.string().nullable(),
  programName: z.string().nullable(),
  tranches: z.array(FeeTrancheSchema),
  totalMillimes: Millimes,
  /** Enrollments of the year that follow this schedule but have no installments yet. */
  missing: z.number().int(),
});
export type FeeSchedule = z.infer<typeof FeeScheduleSchema>;

export const FeeScheduleInputSchema = z.object({
  academicYearId: z.uuid(),
  programId: z.uuid().nullable(),
  tranches: z
    .array(FeeTrancheSchema)
    .min(1)
    .max(12)
    .refine((ts) => ts.every((t, i) => i === 0 || (ts[i - 1]?.dueOn ?? '') <= t.dueOn), {
      message: 'finance.errors.trancheOrder',
    }),
});
export type FeeScheduleInput = z.infer<typeof FeeScheduleInputSchema>;

export const ApplyScheduleResultSchema = z.object({ created: z.number().int() });

// ── Payments ────────────────────────────────────────────────────────────────
export const PaymentSchema = z.object({
  id: z.uuid(),
  installmentId: z.uuid(),
  amountMillimes: Millimes,
  method: z.enum(PAYMENT_METHODS),
  status: z.enum(PAYMENT_STATUSES),
  chequeNumber: z.string().nullable(),
  bank: z.string().nullable(),
  reference: z.string().nullable(),
  valueDate: z.string().nullable(),
  receiptNumber: z.string().nullable(),
  receiptDocumentId: z.uuid().nullable(),
  receivedAt: z.string(),
  receivedByName: z.string().nullable(),
  clearedAt: z.string().nullable(),
  rejectedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
});
export type Payment = z.infer<typeof PaymentSchema>;

export const ReminderSchema = z.object({
  channel: z.enum(REMINDER_CHANNELS),
  at: z.string(),
  auto: z.boolean(),
});

export const InstallmentSchema = z.object({
  id: z.uuid(),
  seq: z.number().int(),
  label: z.string(),
  note: z.string().nullable(),
  dueOn: z.string(),
  amountMillimes: Millimes,
  paidMillimes: Millimes,
  pendingMillimes: Millimes,
  remainingMillimes: Millimes,
  status: z.enum(INSTALLMENT_STATUSES),
  daysLate: z.number().int(),
  payments: z.array(PaymentSchema),
  lastReminder: ReminderSchema.nullable(),
});
export type Installment = z.infer<typeof InstallmentSchema>;

/** Échéancier of one student for one year (design ScrFinance « Échéancier étudiant »). */
export const StudentAccountSchema = z.object({
  student: z.object({
    id: z.uuid(),
    matricule: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().nullable(),
    programName: z.string().nullable(),
    levelCode: z.string().nullable(),
    groupName: z.string().nullable(),
    guardianName: z.string().nullable(),
    guardianPhone: z.string().nullable(),
  }),
  enrollmentId: z.uuid().nullable(),
  yearLabel: z.string().nullable(),
  totalMillimes: Millimes,
  paidMillimes: Millimes,
  pendingMillimes: Millimes,
  remainingMillimes: Millimes,
  installments: z.array(InstallmentSchema),
  /** True when a schedule applies but installments were never generated. */
  canGenerate: z.boolean(),
});
export type StudentAccount = z.infer<typeof StudentAccountSchema>;

const opt = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v ? v : undefined));

export const PaymentCreateSchema = z
  .object({
    installmentId: z.uuid(),
    amountMillimes: PositiveMillimes,
    method: z.enum(PAYMENT_METHODS),
    chequeNumber: opt(30),
    bank: opt(60),
    reference: opt(60),
    valueDate: IsoDate.optional(),
  })
  .superRefine((p, ctx) => {
    if (p.method === 'cheque' && !p.chequeNumber)
      ctx.addIssue({ code: 'custom', path: ['chequeNumber'], message: 'finance.errors.chequeNumber' });
    if (p.method === 'cheque' && !p.bank)
      ctx.addIssue({ code: 'custom', path: ['bank'], message: 'finance.errors.bank' });
    if (p.method === 'transfer' && !p.reference)
      ctx.addIssue({ code: 'custom', path: ['reference'], message: 'finance.errors.reference' });
  });
export type PaymentCreate = z.input<typeof PaymentCreateSchema>;

export const PaymentResultSchema = z.object({
  payment: PaymentSchema,
  account: StudentAccountSchema,
});

export const CancelPaymentSchema = z.object({
  reason: z.string().trim().max(300).optional(),
});

// ── Payments journal ────────────────────────────────────────────────────────
export const PaymentListQuerySchema = PageQuerySchema.extend({
  status: z.enum(PAYMENT_STATUSES).optional(),
  method: z.enum(PAYMENT_METHODS).optional(),
});
export const PaymentRowSchema = PaymentSchema.extend({
  studentId: z.uuid(),
  matricule: z.string(),
  studentName: z.string(),
  installmentLabel: z.string(),
});
export type PaymentRow = z.infer<typeof PaymentRowSchema>;
export const PaymentPageSchema = pageOf(PaymentRowSchema);

// ── Unpaid & reminders ──────────────────────────────────────────────────────
export const UnpaidQuerySchema = PageQuerySchema.extend({
  programId: z.uuid().optional(),
  /** Only installments late by at least this many days. */
  minDays: z.coerce.number().int().min(0).max(365).optional(),
});
export type UnpaidQuery = z.infer<typeof UnpaidQuerySchema>;

export const UnpaidRowSchema = z.object({
  installmentId: z.uuid(),
  studentId: z.uuid(),
  matricule: z.string(),
  studentName: z.string(),
  programCode: z.string().nullable(),
  programName: z.string().nullable(),
  label: z.string(),
  dueOn: z.string(),
  remainingMillimes: Millimes,
  daysLate: z.number().int(),
  hasEmail: z.boolean(),
  lastReminder: ReminderSchema.nullable(),
});
export type UnpaidRow = z.infer<typeof UnpaidRowSchema>;

export const UnpaidPageSchema = pageOf(UnpaidRowSchema).extend({
  kpis: z.object({
    unpaidMillimes: Millimes,
    students: z.number().int(),
    avgDaysLate: z.number(),
    recoveredThisMonthMillimes: Millimes,
  }),
});
export type UnpaidPage = z.infer<typeof UnpaidPageSchema>;

export const ReminderRequestSchema = z.object({
  installmentIds: z.array(z.uuid()).min(1).max(500),
  channels: z.array(z.enum(REMINDER_CHANNELS)).min(1),
  /** Optional note added to the generated message (amount, due date and name are filled per student). */
  message: z.string().trim().max(1000).optional(),
});
export type ReminderRequest = z.infer<typeof ReminderRequestSchema>;
export const ReminderResultSchema = z.object({ sent: z.number().int(), skipped: z.number().int() });

/** What the payment and reminder dialogs need from the tenant settings (finance staff can't read settings). */
export const FinanceOptionsSchema = z.object({
  banks: z.array(z.string()),
  onlinePayment: z.boolean(),
  /** Reminder channels that can actually be used (e-mail; SMS / WhatsApp once their gateways exist). */
  channels: z.array(z.enum(REMINDER_CHANNELS)),
  autoReminders: z.object({ auto: z.boolean(), daysAfterDue: z.array(z.number().int()) }),
});
export type FinanceOptions = z.infer<typeof FinanceOptionsSchema>;
